import { IReadmeVersion } from '_/common/types/readme.types';
import { ICollection, ICollectionCheck } from '_/types/collections/collections.types';
import { ICollectionSettings } from '_/types/collections/collectionSettings.types';
import { IDestination } from '_/types/destinations/destinations.types';
import { ISnapshot } from '_/types/snapshots/snapshot.types';
import zeusSdk from '_/utils/SDKs';

export const fetchAllOrgCollections = async (): Promise<ICollection[]> => {
  try {
    const options = {
      queryParameters: {
        sort: 'name',
        sortDirection: 'ASC',
      },
      include: 'editor,assignedUser,schema',
    };
    const collections = await zeusSdk.collections.listAll(options);
    return Array.isArray(collections) ? collections : [];
  } catch (error) {
    console.error('Error fetching organization collections', error);
    return [];
  }
};

export const fetchProjectCollections = async (projectId: string): Promise<ICollection[]> => {
  try {
    const options = {
      queryParameters: {
        sort: 'name',
        sortDirection: 'ASC',
      },
      include: 'editor,assignedUser,schema',
    };
    const response = await zeusSdk.projects.getAllChildren(projectId, 'collections', options);
    return Array.isArray(response) ? response : [];
  } catch (error) {
    console.error('Error fetching project collections', error);
    return [];
  }
};

export const fetchCollection = async (collectionSlug: string, projectId: string): Promise<ICollection> => {
  try {
    const collection = await zeusSdk.projects.getChildById(projectId, 'collections', collectionSlug, {
      include: 'editor,assignedUser,schema',
    });
    return collection;
  } catch (error) {
    console.error('Error fetching collection', error);
    throw error;
  }
};

export const fetchCollectionReadme = async (collectionId: string): Promise<string> => {
  try {
    const readme = await zeusSdk.collections.getChild(collectionId, 'readme');
    return readme?.content || '';
  } catch (error) {
    console.error('Error fetching collection readme', error);
    return '';
  }
};

export const createCollection = async (
  collection: ICollection,
  projectId: string,
  collectionReadme: string,
): Promise<ICollection> => {
  const parameters = Array.isArray(collection.parameters)
    ? collection.parameters
    : (collection.parameters as string).split(',').map((i: string) => i.trim()) || [];

  const newCollection = await zeusSdk.collections.create({
    ...collection,
    projectId: projectId,
    parameters: parameters,
  });

  if (collectionReadme) {
    await zeusSdk.collections.createChild(newCollection.id, 'readme', {
      content: collectionReadme,
    });
  }

  return newCollection;
};

export const updateCollection = async (
  collectionId: string,
  collection?: ICollection,
  collectionReadme?: string,
): Promise<void> => {
  if (collection?.parameters) {
    collection.parameters = Array.isArray(collection.parameters)
      ? collection.parameters
      : (collection.parameters as string).split(',').map((i: string) => i.trim());
  }

  if (collection) {
    await zeusSdk.collections.patch(collectionId, collection);
  }

  if (collectionReadme) {
    await zeusSdk.collections.updateChild(collectionId, 'readme', {
      content: collectionReadme,
    });
  }
};

export const fetchCollectionVersions = async (
  collectionId: string,
  cursor?: string | null,
  limit = 5,
): Promise<{ items: any[]; cursor: string | null }> => {
  try {
    const options = {
      queryParameters: {
        limit: limit,
        sortDirection: 'DESC',
        sort: 'timestamp',
        ...(cursor ? { cursor: cursor } : {}),
      },
    };

    const response = await zeusSdk.collections.getChild(collectionId, '_versions', options);
    return {
      items: response?.items || [],
      cursor: response?.cursor || null,
    };
  } catch (error) {
    console.error('Error fetching collection versions', error);
    return { items: [], cursor: null };
  }
};

export const fetchCollectionReadmeVersions = async (collectionId: string): Promise<IReadmeVersion[]> => {
  try {
    const response = await zeusSdk.collections.getChild(collectionId, 'readme/_versions');
    return response?.items || [];
  } catch (error) {
    console.error('Error fetching collection readme versions', error);
    return [];
  }
};

export const fetchLinkedCollectionDestinations = async (collectionId: string): Promise<IDestination[]> => {
  try {
    const response = await zeusSdk.collections.getChildren(collectionId, 'destinations');
    return response?.items || [];
  } catch (error) {
    console.error('Error fetching collection destinations', error);
    return [];
  }
};

export const linkDestinationToCollection = async (collectionId: string, destinationId: string): Promise<void> => {
  await zeusSdk.collections.linkDestination(collectionId, destinationId);
};

export const unlinkDestinationFromCollection = async (collectionId: string, destinationId: string): Promise<void> => {
  await zeusSdk.collections.unlinkDestination(collectionId, destinationId);
};

// Collection Checks
export const fetchCollectionChecks = async (collectionId: string): Promise<ICollectionCheck[]> => {
  try {
    const response = await zeusSdk.collections.getChecks(collectionId, {
      queryParameters: { limit: 1000 },
    });
    return response?.items || [];
  } catch (error) {
    console.error('Error fetching collection checks', error);
    return [];
  }
};

export const createCollectionCheck = async (collectionCheck: ICollectionCheck): Promise<any> => {
  return zeusSdk.collectionQaTests.create(collectionCheck);
};

export const updateCollectionCheck = async (collectionCheckId: string, collectionCheck: ICollectionCheck): Promise<void> => {
  await zeusSdk.collectionQaTests.patch(collectionCheckId, collectionCheck);
};

export const deleteCollectionCheck = async (collectionCheckId: string): Promise<void> => {
  await zeusSdk.collectionQaTests.delete(collectionCheckId);
};

// Collection Settings API
export const fetchCollectionSettings = async (collectionId: string): Promise<Partial<ICollectionSettings>> => {
  try {
    const settings = await zeusSdk.collections.getSettings(collectionId);
    return settings || {};
  } catch (error: any) {
    if (error.status === 404) {
      return {};
    }
    console.error('Error fetching collection settings', error);
    throw error;
  }
};

export const saveCollectionSettings = async (
  collectionId: string,
  settings: Partial<ICollectionSettings>,
  isNew: boolean,
): Promise<any> => {
  if (isNew) {
    return zeusSdk.collections.addSettings(collectionId, settings);
  } else {
    return zeusSdk.collections.updateSettings(collectionId, settings);
  }
};

// Collection Pushes API
export const fetchCollectionPushes = async (collectionId: string): Promise<any[]> => {
  try {
    return await zeusSdk.collections.getPushes(collectionId);
  } catch (error) {
    console.error('Error fetching collection pushes', error);
    return [];
  }
};

// Collection Snapshots API
export const fetchCollectionSnapshots = async (
  collectionId: string,
  filter = 'status||in||PENDING_QA,QA,PASSED_QA,IN_PROGRESS,FAILED,FIXING,ESCALATED,FAILED_QA',
  limit = 50,
  cursor?: string,
): Promise<{ items: ISnapshot[]; cursor?: string }> => {
  try {
    const options = {
      queryParameters: {
        limit: limit,
        filter: filter,
        sort: 'addedAt',
        sortDirection: 'DESC',
        ...(cursor ? { cursor: cursor } : {}),
      },
      include: 'source.collection.project,importStatus,editor,assignedUser,parent',
    };

    const response = await zeusSdk.collections.getChildren(collectionId, 'snapshots', options);
    return {
      items: response?.items || [],
      cursor: response?.metadata?.cursor,
    };
  } catch (error) {
    console.error('Error fetching collection snapshots', error);
    return { items: [], cursor: undefined };
  }
};
